import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { variance } from 'mathjs';
import { Container, Col, Div, Row, Tag, Text } from '@pinpoint/design-kit';

import { pxScoresPropType, stylePropType } from 'src/PropTypes';
import { Style1 as Style } from 'src/styles';

import Radar from 'src/charts/radar';
import Heatmap from 'src/charts/heatmap';
import Slider from 'src/charts/slider';

import { withFixedTitle, EventEmitter } from 'src/components';

const eventEmitter = new EventEmitter();

/* eslint-disable react/prop-types */

function TextualContent({ trait: initialTrait, labels, series, theme, onThemeChange, minScore, maxScore }) {
  const [selectedtrait, setSelectedTrait] = useState(initialTrait);
  eventEmitter.subscribe('selectedTrait', (trait) => {
    setSelectedTrait(trait);
  });
  const { code, subType, trait, description, factor, highRangeName, lowRangeName } = selectedtrait;
  const index = labels.findIndex(({ code: labelCode }) => code === labelCode);
  const selectedSeries = series.map(({ name, theme: segmentTheme, values }) => ({
    name,
    theme: segmentTheme,
    value: values[index],
  }));
  return (
    <Div h="100%" d="flex" flexDir="column" justify="start" bg="" overflow="auto">
      <Div d="flex" justify={{ xs: 'center', md: 'space-between' }} flexDir="row" align="center" m={{ b: '1.25rem' }}>
        {subType === 'factor' ? (
          <Div>
            <Text fontFamily="secondary" textSize="title" textWeight="400" tag="span">
              {`${subType.charAt(0).toUpperCase() + subType.slice(1)}: `}
            </Text>
            <Text fontFamily="secondary" textSize="subheader" textWeight="400" textColor="brand500" tag="span">
              {trait}
            </Text>
          </Div>
        ) : (
          <>
            <Div>
              <Text fontFamily="secondary" textSize="title" textWeight="400" tag="span" textColor="tabSelected">
                Facet:&nbsp;
              </Text>
              <Text fontFamily="secondary" textSize="title" textWeight="400" textColor="brand500" tag="span">
                {trait}
              </Text>
            </Div>
            <Div>
              <Tag
                style={{
                  border: '2px solid',
                  borderColor: Style.COLORS[factor.toUpperCase()].PRIMARY,
                  color: '#666',
                  padding: '4px 8px',
                  fontWeight: '400',
                }}
              >
                <Text textColor={Style.COLORS[factor.toUpperCase()].PRIMARY}>{factor && factor.toUpperCase()}</Text>
              </Tag>
            </Div>
          </>
        )}
      </Div>
      <Div rounded="md">
        <Slider
          label={{
            trait,
            highRangeName,
            lowRangeName,
          }}
          series={selectedSeries}
          theme={theme}
          onThemeChange={onThemeChange}
          min={minScore}
          max={maxScore}
        />
      </Div>
      <Div m={{ y: '1.5rem' }}>
        <Text textSize="subheader" textWeight="400">
          {description}
        </Text>
      </Div>
    </Div>
  );
}

const SquishlessHeatmap = withFixedTitle(Heatmap);
const SquishlessRadar = withFixedTitle(Radar);

/* eslint-disable jsx-a11y/mouse-events-have-key-events */

function Top7Content({ labels, series, style, ...rest }) {
  return (
    <SquishlessRadar
      title={series.length === 1 ? 'Actionable Facets' : 'Divergent Facets'}
      labels={labels}
      series={series}
      style={style}
      onMouseover={({ label, values }) => {
        eventEmitter.dispatch('selectedTrait', label);
        eventEmitter.dispatch('traitValues', values);
      }}
      {...rest}
    />
  );
}

function HeatmapContent({ labels, series, strength, style, ...rest }) {
  return (
    <SquishlessHeatmap
      title={series.length === 1 ? 'Facets Strength' : 'Facets Divergence'}
      labels={labels}
      series={series}
      strength={strength}
      style={style}
      onMouseover={({ label, values }) => {
        eventEmitter.dispatch('selectedTrait', label);
        eventEmitter.dispatch('traitValues', values);
      }}
      options={{
        grid: {
          top: '15%',
        },
      }}
      {...rest}
    />
  );
}

/* eslint-enable jsx-a11y/mouse-events-have-key-events */
/* eslint-enable react/prop-types */

function Segments({ objects: _objects, style, minW, minH, theme: themeProp, defaultTheme }) {
  const [theme, setTheme] = useState(themeProp);

  if (_objects.length === 0) {
    return <Div>No Data</Div>;
  }

  const objects = [];
  const segments = [];
  _objects.forEach(({ pxScores, ...rest }) => {
    const pxScoresCopy = pxScores.filter(({ trait: { subType } }) => subType === 'facet');
    pxScoresCopy.sort(({ trait: { code: codeA } }, { trait: { code: codeB } }) => (codeA > codeB ? 1 : -1));
    objects.push({
      ...rest,
      pxScores: pxScoresCopy,
    });
  });

  const traits = objects[0].pxScores.map(({ trait }) => trait);
  let maxScore = 0;
  let minScore = 100;
  for (let i = 0; i < traits.length; i += 1) {
    const scores = objects.map(({ pxScores }) => pxScores[i].score);
    const localMin = Math.min(...scores);
    minScore = localMin < minScore ? localMin : minScore;
    const localMax = Math.max(...scores);
    maxScore = localMax > maxScore ? localMax : maxScore;
    segments.push({
      trait: traits[i],
      scores,
      order: scores.length > 1 ? variance(...scores) || 0.01 : Math.abs(50 - scores[0]),
    });
  }
  segments.sort(({ order: orderA }, { order: orderB }) => orderB - orderA);
  // Isolate the labels
  const labels = segments.map(({ trait }) => trait);
  const strength = segments.map(({ order }) => order);
  const series = [];
  for (let i = 0; i < objects.length; i += 1) {
    series.push({
      name: objects[i].name,
      values: segments.map(({ scores }) => scores[i]),
      theme: objects[i].theme || 'pinpoint',
    });
  }
  const top7Labels = labels.slice(0, 7);
  const top7Series = series.map(({ name, theme: segmentTheme, values }) => ({
    name,
    theme: segmentTheme,
    values: values.slice(0, 7),
  }));

  return (
    <Container h="100%" minH={minH} w="100%" minW={minW} bg="secondary">
      <Row minH="35vh" h="100%" w="100%">
        <Col size={{ xs: '12', lg: '6' }} h="100%" gutterWidth="1.25rem" data-test="col-0">
          <Div
            data-test="col-0-div"
            minH="400px"
            h="calc(50% - 1.25rem)"
            w="100%"
            rounded="md"
            m={{ b: '1.25rem' }}
            bg="white"
            p={{ x: '1.5rem', y: '1.25rem' }}
          >
            <TextualContent
              data-test="col-0-div-text"
              trait={labels[0]}
              labels={labels}
              series={series}
              style={style}
              theme={theme}
              onThemeChange={(nextTheme) => {
                setTheme(nextTheme);
              }}
              minScore={minScore}
              maxScore={maxScore}
            />
          </Div>
          <Div
            data-test="col-0-0-div"
            minH="400px"
            h="calc(50%)"
            w="100%"
            m={{ t: '0.5rem' }}
            bg="white"
            p={{ x: '1.5rem', y: '1.25rem' }}
            rounded="md"
          >
            <Top7Content
              data-test="col-0-0-div-content"
              labels={top7Labels}
              series={top7Series}
              style={style}
              theme={theme}
              defaultTheme={defaultTheme}
              relative
              onThemeChange={(nextTheme) => {
                setTheme(nextTheme);
              }}
              options={{
                legend: {
                  padding: [0, 0, 8, 0],
                  textStyle: {
                    fontSize: 12,
                  },
                },
              }}
            />
          </Div>
        </Col>
        <Col size={{ xs: '12', lg: '6' }} h="100%" gutterWidth="1.25rem" data-test="col-1">
          <Div
            minH="800px"
            h="100%"
            w="100%"
            rounded="md"
            bg="white"
            p={{ x: '1.5rem', y: '1.25rem' }}
            data-test="col-1-div"
          >
            <HeatmapContent
              data-test="col-1-div-content"
              labels={labels}
              series={series}
              strength={strength}
              style={style}
              titleStyle={{
                m: { b: '0.5rem' },
                fontSize: '1.5rem',
              }}
              theme={theme}
            />
          </Div>
        </Col>
      </Row>
    </Container>
  );
}

Segments.propTypes = {
  objects: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      pxScores: pxScoresPropType,
    })
  ).isRequired,
  style: stylePropType,
  styleOverride: PropTypes.shape({}),
  minH: PropTypes.string,
  minW: PropTypes.string,
  theme: PropTypes.string,
};

Segments.defaultProps = {
  style: Style,
  styleOverride: {
    background: 'white',
    padding: '1.25rem 1.5rem',
  },
  minH: '100vh',
  minW: '100vw',
  theme: 'pinpoint',
};

export default Segments;
